import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Factory, Cpu, Building2, ChevronRight } from 'lucide-react';

const services = [
  {
    icon: Factory,
    title: 'Smart Industry',
    color: '#0057FF',
    description: 'Modernizamos a indústria com tecnologias IoT, automação e análise de dados em tempo real.',
    items: [
      'Sensores IoT & Monitorização',
      'Integração de Sistemas ERP',
      'Big Data Analytics',
      'Business Intelligence',
      'Certificação Energética',
      'Sistemas de Apoio à Decisão',
    ],
  },
  {
    icon: Cpu,
    title: 'Digital Transformation',
    color: '#00C896',
    description: 'Aceleramos a transformação digital com engenharia de software e automação de processos.',
    items: [
      'Engenharia de Software',
      'Automação de Processos',
      'Gestão de Projetos',
      'Consultoria Estratégica',
      'IT Outsourcing',
      'Desenvolvimento Ágil',
    ],
  },
  {
    icon: Building2,
    title: 'Smart Cities',
    color: '#FFE53B',
    description: 'Criamos infraestruturas urbanas inteligentes, sustentáveis e conectadas.',
    items: [
      'Internet das Coisas (IoT)',
      'Green Data Spaces',
      'Agricultura de Precisão',
      'Soluções de Planeamento Urbano',
      'Consultoria de Sustentabilidade',
      'Infraestrutura Inteligente',
    ],
  },
];

const Services: React.FC = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [hovered, setHovered] = useState<number | null>(null);

  return (
    <section id="services" style={styles.section} ref={ref}>
      {/* Section header */}
      <div style={styles.header}>
        <motion.span
          style={styles.label}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          Serviços
        </motion.span>
        <motion.h2
          style={styles.title}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.1 }}
        >
          As nossas <span style={styles.titleAccent}>três áreas</span><br />de especialização
        </motion.h2>
        <motion.p
          style={styles.subtitle}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.2 }}
        >
          Cobrimos o espectro completo da inovação tecnológica — da fábrica à cidade, do processo ao produto.
        </motion.p>
      </div>

      {/* Cards */}
      <div style={styles.grid}>
        {services.map((svc, i) => {
          const Icon = svc.icon;
          const isHovered = hovered === i;
          return (
            <motion.div
              key={i}
              style={{
                ...styles.card,
                borderTop: `3px solid ${svc.color}`,
                transform: isHovered ? 'translateY(-8px)' : 'translateY(0)',
                boxShadow: isHovered
                  ? `0 24px 60px rgba(0,0,0,0.12), 0 0 0 1px ${svc.color}22`
                  : '0 4px 24px rgba(0,0,0,0.06)',
              } as React.CSSProperties}
              initial={{ opacity: 0, y: 40 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.15 * i }}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
            >
              {/* Icon */}
              <div style={{ ...styles.iconWrap, background: `${svc.color}12`, border: `1px solid ${svc.color}30` }}>
                <Icon size={28} color={svc.color} />
              </div>

              <h3 style={styles.cardTitle}>{svc.title}</h3>
              <p style={styles.cardDesc}>{svc.description}</p>

              <ul style={styles.itemList}>
                {svc.items.map((item, j) => (
                  <li key={j} style={styles.item}>
                    <span style={{ ...styles.itemDot, background: svc.color }} />
                    {item}
                  </li>
                ))}
              </ul>

              <button
                style={{ ...styles.learnMore, color: svc.color }}
                onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Saber mais <ChevronRight size={16} />
              </button>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  section: {
    background: '#ffffff',
    padding: '120px 0',
  },
  header: {
    maxWidth: '1200px',
    margin: '0 auto 4rem',
    padding: '0 2rem',
    textAlign: 'center',
  },
  label: {
    fontFamily: "'IBM Plex Sans', sans-serif",
    fontSize: '0.75rem',
    fontWeight: 600,
    letterSpacing: '0.25em',
    textTransform: 'uppercase',
    color: '#00C896',
    marginBottom: '1rem',
    display: 'block',
  },
  title: {
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    fontSize: 'clamp(2rem, 4vw, 3rem)',
    fontWeight: 900,
    color: '#1A2535',
    lineHeight: 1.1,
    marginBottom: '1.25rem',
    letterSpacing: '-0.02em',
  },
  titleAccent: {
    color: '#0057FF',
  },
  subtitle: {
    fontSize: '1rem',
    color: '#516273',
    lineHeight: 1.75,
    maxWidth: '550px',
    margin: '0 auto',
  },
  grid: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 2rem',
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '2rem',
  },
  card: {
    background: '#ffffff',
    padding: '2.5rem',
    transition: 'all 0.3s ease',
    cursor: 'default',
  },
  iconWrap: {
    width: '60px',
    height: '60px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '1.5rem',
  },
  cardTitle: {
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    fontSize: '1.4rem',
    fontWeight: 800,
    color: '#1A2535',
    marginBottom: '0.75rem',
    letterSpacing: '-0.01em',
  },
  cardDesc: {
    fontSize: '0.9rem',
    color: '#516273',
    lineHeight: 1.7,
    marginBottom: '1.5rem',
  },
  itemList: {
    listStyle: 'none',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.6rem',
    marginBottom: '2rem',
    paddingTop: '1rem',
    borderTop: '1px solid #F3F6FB',
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.6rem',
    fontSize: '0.85rem',
    fontWeight: 500,
    color: '#1A2535',
  },
  itemDot: {
    width: '5px',
    height: '5px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  learnMore: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.3rem',
    fontFamily: "'IBM Plex Sans', sans-serif",
    fontSize: '0.8rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    transition: 'gap 0.2s',
  },
};

export default Services;
